import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, ArrowRight, AlertCircle } from "lucide-react";

const MAX_POLL = 20;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [record, setRecord] = useState(null);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setRecord(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          setStatus("error");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_POLL) { setStatus("timeout"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        attempts += 1;
        if (attempts >= MAX_POLL) { setStatus("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-6" data-testid="payment-success-page">
      <Card className="w-full max-w-lg p-10 text-center border-black/10">
        {status === "polling" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
            <h1 className="mt-6 font-display text-2xl font-bold">Confirmation du paiement...</h1>
            <p className="mt-2 text-sm text-muted-foreground">Cela prend quelques secondes.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <span className="h-14 w-14 mx-auto grid place-items-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">Paiement confirmé</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {record?.lookup_key === "premium_monthly"
                ? "Votre abonnement Premium est actif. Tous vos rapports sont maintenant débloqués."
                : "Votre rapport détaillé est débloqué."}
            </p>
            <div className="mt-8 flex flex-col gap-3">
              {record?.diagnostic_id ? (
                <Button onClick={() => navigate(`/results/${record.diagnostic_id}`)} className="rounded-full h-11" data-testid="success-view-report">
                  Voir mon rapport <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={() => navigate("/dashboard")} className="rounded-full h-11" data-testid="success-view-dashboard">
                  Aller au tableau de bord <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <span className="h-14 w-14 mx-auto grid place-items-center rounded-full bg-amber-100 text-amber-700">
              <AlertCircle className="h-8 w-8" />
            </span>
            <h1 className="mt-6 font-display text-2xl font-bold">Confirmation en cours</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              La confirmation Stripe prend plus de temps que prévu. Rafraîchissez la page dans un instant, votre paiement est en cours de traitement.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <Button onClick={() => window.location.reload()} className="rounded-full h-11">Rafraîchir</Button>
              <Link to="/dashboard" className="text-sm text-primary hover:underline">Retour au tableau de bord</Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
